import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Order, OrderItem } from '@/types';
import { ordersApi } from '@/services/api';
import { fetchCart } from './cartSlice';
import toast from 'react-hot-toast';

interface OrdersState {
  orders: Order[];
  currentOrder: Order | null;
  isLoading: boolean;
  error: string | null;
  pagination: {
    page: number;
    total: number;
    pages: number;
  };
}

const initialState: OrdersState = {
  orders: [],
  currentOrder: null,
  isLoading: false,
  error: null,
  pagination: {
    page: 1,
    total: 0,
    pages: 0,
  },
};

// Async thunks
export const fetchOrders = createAsyncThunk(
  'orders/fetchOrders',
  async (_, { rejectWithValue }) => {
    try {
      const response = await ordersApi.getAll();
      return response.data;
    } catch (error: any) {
      // Safely extract error message
      let message = 'Failed to fetch orders';
      if (error?.detail) {
        if (typeof error.detail === 'string') {
          message = error.detail;
        } else if (Array.isArray(error.detail)) {
          message = error.detail.map((err: any) => err.msg || err.message || String(err)).join(', ');
        } else {
          message = String(error.detail);
        }
      } else if (error?.message) {
        message = error.message;
      }
      return rejectWithValue(message);
    }
  }
);

export const fetchOrderById = createAsyncThunk(
  'orders/fetchOrderById',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await ordersApi.getById(id);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.detail || 'Failed to fetch order');
    }
  }
);

export const createFromCart = createAsyncThunk(
  'orders/createFromCart',
  async (orderData: any, { rejectWithValue, dispatch }) => {
    try {
      const response = await ordersApi.createFromCart(orderData);
      toast.success('Order placed successfully!');
      // Refresh cart after successful order to clear it
      dispatch(fetchCart());
      return response.data;
    } catch (error: any) {
      console.error('Order creation error:', error);
      
      // The API interceptor formats errors as { detail, status_code, request_id }
      let message = 'Failed to place order';
      
      if (error.detail) {
        // Error is already formatted by API interceptor
        message = error.detail;
      } else if (error.response?.data?.detail) {
        // Fallback for direct axios errors
        if (Array.isArray(error.response.data.detail)) {
          // Handle validation errors
          message = error.response.data.detail.map((err: any) => err.msg || err.message || err).join(', ');
        } else {
          message = error.response.data.detail;
        }
      } else if (error.message) {
        message = error.message;
      }
      
      // Show error toast with the detailed message
      toast.error(message, {
        duration: 5000, // Show for 5 seconds
        style: {
          maxWidth: '500px',
        },
      });
      
      return rejectWithValue(message);
    }
  }
);

export const updateOrder = createAsyncThunk(
  'orders/updateOrder',
  async ({ id, orderData }: { id: number; orderData: any }, { rejectWithValue }) => {
    try {
      const response = await ordersApi.update(id, orderData);
      toast.success('Order updated successfully');
      return response.data;
    } catch (error: any) {
      const message = error.detail || 'Failed to update order';
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

export const cancelOrder = createAsyncThunk(
  'orders/cancelOrder',
  async (id: number, { rejectWithValue }) => {
    try {
      await ordersApi.cancel(id);
      toast.success('Order cancelled successfully');
      return id;
    } catch (error: any) {
      const message = error.detail || 'Failed to cancel order';
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

export const confirmDelivery = createAsyncThunk(
  'orders/confirmDelivery',
  async (id: number, { rejectWithValue }) => {
    try {
      await ordersApi.confirmDelivery(id);
      toast.success('Delivery confirmed successfully');
      return id;
    } catch (error: any) {
      const message = error.detail || 'Failed to confirm delivery';
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

export const getSellerOrders = createAsyncThunk(
  'orders/getSellerOrders',
  async (_, { rejectWithValue }) => {
    try {
      const response = await ordersApi.getSellerOrders();
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.detail || 'Failed to fetch seller orders');
    }
  }
);

export const updateOrderStatus = createAsyncThunk(
  'orders/updateOrderStatus',
  async ({ id, newStatus }: { id: number; newStatus: string }, { rejectWithValue }) => {
    try {
      await ordersApi.updateStatus(id, newStatus);
      toast.success(`Order status updated to ${newStatus}`);
      return { id, newStatus };
    } catch (error: any) {
      // Safely extract error message
      let message = 'Failed to update order status';
      if (error?.detail) {
        if (typeof error.detail === 'string') {
          message = error.detail;
        } else if (Array.isArray(error.detail)) {
          message = error.detail.map((err: any) => err.msg || err.message || String(err)).join(', ');
        } else {
          message = String(error.detail);
        }
      } else if (error?.message) {
        message = error.message;
      }
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

export const payCommission = createAsyncThunk(
  'orders/payCommission',
  async ({ id, commissionData }: { id: number; commissionData: any }, { rejectWithValue }) => {
    try {
      const response = await ordersApi.payCommission(id, commissionData);
      toast.success('Commission paid successfully');
      return response.data;
    } catch (error: any) {
      const message = error.detail || 'Failed to pay commission';
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

export const getBuyerContact = createAsyncThunk(
  'orders/getBuyerContact',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await ordersApi.getBuyerContact(id);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.detail || 'Failed to get buyer contact');
    }
  }
);

const ordersSlice = createSlice({
  name: 'orders',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentOrder: (state, action: PayloadAction<Order | null>) => {
      state.currentOrder = action.payload;
    },
    clearCurrentOrder: (state) => {
      state.currentOrder = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Orders
      .addCase(fetchOrders.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchOrders.fulfilled, (state, action) => {
        state.isLoading = false;
        state.orders = action.payload;
        state.error = null;
      })
      .addCase(fetchOrders.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Fetch Order by ID
      .addCase(fetchOrderById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchOrderById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentOrder = action.payload;
        state.error = null;
      })
      .addCase(fetchOrderById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Create Order from Cart
      .addCase(createFromCart.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createFromCart.fulfilled, (state, action) => {
        state.isLoading = false;
        state.orders.unshift(action.payload);
        state.currentOrder = action.payload;
        state.error = null;
      })
      .addCase(createFromCart.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Update Order
      .addCase(updateOrder.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateOrder.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.orders.findIndex(order => order.id === action.payload.id);
        if (index !== -1) {
          state.orders[index] = action.payload;
        }
        if (state.currentOrder?.id === action.payload.id) {
          state.currentOrder = action.payload;
        }
        state.error = null;
      })
      .addCase(updateOrder.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Cancel Order
      .addCase(cancelOrder.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(cancelOrder.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.orders.findIndex(order => order.id === action.payload);
        if (index !== -1) {
          state.orders[index].order_status = 'cancelled';
        }
        if (state.currentOrder?.id === action.payload) {
          state.currentOrder.order_status = 'cancelled';
        }
        state.error = null;
      })
      .addCase(cancelOrder.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Confirm Delivery
      .addCase(confirmDelivery.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(confirmDelivery.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.orders.findIndex(order => order.id === action.payload);
        if (index !== -1) {
          state.orders[index].order_status = 'delivered';
        }
        if (state.currentOrder?.id === action.payload) {
          state.currentOrder.order_status = 'delivered';
        }
        state.error = null;
      })
      .addCase(confirmDelivery.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Get Seller Orders
      .addCase(getSellerOrders.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getSellerOrders.fulfilled, (state, action) => {
        state.isLoading = false;
        state.orders = action.payload;
        state.error = null;
      })
      .addCase(getSellerOrders.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Update Order Status
      .addCase(updateOrderStatus.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateOrderStatus.fulfilled, (state, action) => {
        state.isLoading = false;
        const { id, newStatus } = action.payload;
        const index = state.orders.findIndex(order => order.id === id);
        if (index !== -1) {
          state.orders[index].order_status = newStatus as any;
        }
        if (state.currentOrder?.id === id) {
          state.currentOrder.order_status = newStatus as any;
        }
        state.error = null;
      })
      .addCase(updateOrderStatus.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Pay Commission
      .addCase(payCommission.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(payCommission.fulfilled, (state, action) => {
        state.isLoading = false;
        const orderId = action.payload.order_id;
        const index = state.orders.findIndex(order => order.id === orderId);
        if (index !== -1) {
          state.orders[index].commission_paid = true;
        }
        if (state.currentOrder?.id === orderId) {
          state.currentOrder.commission_paid = true;
        }
        state.error = null;
      })
      .addCase(payCommission.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, setCurrentOrder, clearCurrentOrder } = ordersSlice.actions;
export { ordersSlice };
export default ordersSlice.reducer;
